import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader } from "../components/common";
import { toast } from "sonner";
import { Loader2, Save } from "lucide-react";

const Field = ({ label, children }) => (
  <div>
    <label className="block text-sm font-medium text-slate-700 mb-1.5">{label}</label>
    {children}
  </div>
);
const inputCls = "w-full px-3.5 py-2.5 rounded-md border border-slate-300 bg-white focus:border-[#00427A] focus:ring-2 focus:ring-[#00427A]/20 outline-none";

export default function Profile() {
  const { user, meta } = useAuth();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/profile/me").then(({ data }) => {
      const base = data || { name: user.name, surname: user.surname, email: user.email, province: "Northern Cape" };
      base.sectors = base.sectors && base.sectors.length
        ? base.sectors
        : (base.sector ? String(base.sector).split(/,\s*/).filter(Boolean) : []);
      setForm(base);
    });
  }, [user]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        name: form.name, surname: form.surname, company_name: form.company_name || "",
        contact_number: form.contact_number || "", email: form.email,
        tax_number: form.tax_number || "", registration_number: form.registration_number || "",
        bee_level: form.bee_level || "", province: form.province || "Northern Cape",
        municipality: form.municipality || "", district: form.district || "",
        town: form.town || "", sector: (form.sectors || []).join(", "),
        sectors: form.sectors || [],
      };
      const { data } = await api.put("/profile", payload);
      setForm(data);
      toast.success("Company profile saved");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  if (!form) return <Loader2 className="h-6 w-6 animate-spin text-[#00427A]" />;

  return (
    <div>
      <PageHeader eyebrow="SMME" title="Company Profile"
        subtitle="Keep your company details accurate — buyers search this information." />
      <form onSubmit={save} className="bg-white rounded-xl border border-slate-200 p-6 sm:p-8 space-y-7">
        <section>
          <h3 className="font-display font-bold text-slate-900 mb-4">Contact Person</h3>
          <div className="grid sm:grid-cols-2 gap-5">
            <Field label="Name"><input data-testid="profile-name-input" className={inputCls} value={form.name || ""} onChange={set("name")} required /></Field>
            <Field label="Surname"><input data-testid="profile-surname-input" className={inputCls} value={form.surname || ""} onChange={set("surname")} required /></Field>
            <Field label="Email Address"><input data-testid="profile-email-input" type="email" className={inputCls} value={form.email || ""} onChange={set("email")} required /></Field>
            <Field label="Contact Number"><input data-testid="profile-contact-input" className={inputCls} value={form.contact_number || ""} onChange={set("contact_number")} placeholder="+27 …" /></Field>
          </div>
        </section>

        <section>
          <h3 className="font-display font-bold text-slate-900 mb-4">Company Details</h3>
          <div className="grid sm:grid-cols-2 gap-5">
            <Field label="Company Name"><input data-testid="profile-company-name-input" className={inputCls} value={form.company_name || ""} onChange={set("company_name")} required /></Field>
            <Field label="Business Sector (choose up to 2)">
              <div className="max-h-64 overflow-y-auto space-y-1.5 border border-slate-200 rounded-md p-3">
                {(meta?.sectors || []).map((s) => {
                  const checked = (form.sectors || []).includes(s);
                  return (
                    <label key={s} data-testid={`profile-sector-checkbox-${s}`}
                      className={`flex items-center gap-3 px-3 py-2 rounded-md border cursor-pointer transition-colors ${
                        checked ? "border-[#00427A] bg-[#00427A]/5" : "border-transparent hover:bg-slate-50"
                      }`}>
                      <input type="checkbox" className="h-4 w-4 accent-[#00427A]" checked={checked}
                        onChange={() => {
                          const cur = form.sectors || [];
                          if (checked) { setForm({ ...form, sectors: cur.filter((x) => x !== s) }); }
                          else if (cur.length >= 2) { toast.error("You can select up to 2 sectors"); }
                          else { setForm({ ...form, sectors: [...cur, s] }); }
                        }} />
                      <span className="text-sm text-slate-800">{s}</span>
                    </label>
                  );
                })}
              </div>
            </Field>
            <Field label="Tax Number"><input data-testid="profile-tax-number-input" className={`${inputCls} font-mono-data`} value={form.tax_number || ""} onChange={set("tax_number")} /></Field>
            <Field label="Company Registration Number"><input data-testid="profile-registration-number-input" className={`${inputCls} font-mono-data`} value={form.registration_number || ""} onChange={set("registration_number")} /></Field>
            <Field label="BEE Level">
              <select data-testid="profile-bee-level-select" className={inputCls} value={form.bee_level || ""} onChange={set("bee_level")}>
                <option value="">Select BEE level…</option>
                {(meta?.bee_levels || []).map((b) => <option key={b} value={b}>{b}</option>)}
              </select>
            </Field>
          </div>
        </section>

        <section>
          <h3 className="font-display font-bold text-slate-900 mb-4">Location</h3>
          <div className="grid sm:grid-cols-2 gap-5">
            <Field label="Province">
              <select data-testid="profile-province-select" className={inputCls} value={form.province || "Northern Cape"} onChange={set("province")}>
                {(meta?.provinces || ["Northern Cape"]).map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </Field>
            <Field label="District">
              <div className="space-y-2">
                {(meta?.districts || []).map((d) => (
                  <label key={d} data-testid={`profile-district-checkbox-${d}`}
                    className={`flex items-center gap-3 px-3.5 py-2.5 rounded-md border cursor-pointer transition-colors ${
                      form.district === d ? "border-[#00427A] bg-[#00427A]/5" : "border-slate-300 hover:border-slate-400"
                    }`}>
                    <input type="checkbox" className="h-4 w-4 accent-[#00427A]"
                      checked={form.district === d}
                      onChange={() => setForm({ ...form, district: form.district === d ? "" : d })} />
                    <span className="text-sm text-slate-800">{d}</span>
                  </label>
                ))}
              </div>
            </Field>
            <Field label="Municipality Name">
              <div className="space-y-2">
                {(form.district
                  ? (meta?.municipalities?.[form.district] || [])
                  : Object.values(meta?.municipalities || {}).flat()
                ).map((m) => (
                  <label key={m} data-testid={`profile-municipality-checkbox-${m}`}
                    className={`flex items-center gap-3 px-3.5 py-2.5 rounded-md border cursor-pointer transition-colors ${
                      form.municipality === m ? "border-[#00427A] bg-[#00427A]/5" : "border-slate-300 hover:border-slate-400"
                    }`}>
                    <input type="checkbox" className="h-4 w-4 accent-[#00427A]"
                      checked={form.municipality === m}
                      onChange={() => setForm({ ...form, municipality: form.municipality === m ? "" : m })} />
                    <span className="text-sm text-slate-800">{m}</span>
                  </label>
                ))}
              </div>
            </Field>
            <Field label="Town / Village">
              <div className="max-h-56 overflow-y-auto space-y-1.5 border border-slate-200 rounded-md p-3">
                {(meta?.towns || []).map((t) => (
                  <label key={t} data-testid={`profile-town-checkbox-${t}`}
                    className={`flex items-center gap-3 px-3 py-2 rounded-md border cursor-pointer transition-colors ${
                      form.town === t ? "border-[#00427A] bg-[#00427A]/5" : "border-transparent hover:bg-slate-50"
                    }`}>
                    <input type="checkbox" className="h-4 w-4 accent-[#00427A]"
                      checked={form.town === t}
                      onChange={() => setForm({ ...form, town: form.town === t ? "" : t })} />
                    <span className="text-sm text-slate-800">{t}</span>
                  </label>
                ))}
              </div>
            </Field>
          </div>
        </section>

        <div className="flex justify-end pt-2">
          <button data-testid="profile-save-button" disabled={saving}
            className="inline-flex items-center gap-2 bg-[#00427A] hover:bg-[#00325C] text-white font-semibold px-6 py-2.5 rounded-md transition-colors disabled:opacity-60">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save Profile
          </button>
        </div>
      </form>
    </div>
  );
}
